import { SyncCoreDataDefinitionsEnumTranslator, SyndicationType } from '@edgebox/sync-core-data-definitions';
import { ClientSyndicationEntity } from '@edgebox/sync-core-rest-client';
import React from 'react';
import { EntityTypeVersionName } from './EntityTypeVersionName';
import { SiteName } from './SiteName';

interface IProps {
  entity: ClientSyndicationEntity;
  showEntityDetails?: boolean;
  showSiteName?: boolean;
  showType?: boolean;
  showDirection?: boolean;
}

export function getSyndicationTypeName(type: SyndicationType, name?: string) {
  // The definitions name every syndication type they declare, and their word is the one this answers
  // with. A type they do not name is one this app meets before its definitions do, and what it is
  // called on the wire says nothing to a reader: the entity is named without it.
  const typeName = SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue<SyndicationType>('SyndicationType', type) as string | undefined;

  if (name) {
    return typeName ? `${typeName} ${name}` : name;
  }
  return typeName;
}

export class SyndicationName extends React.Component<IProps, {}> {
  render() {
    const { entity, showEntityDetails, showSiteName, showType, showDirection } = this.props;

    if (entity.type === SyndicationType.RetrieveAndPushEntity) {
      return (
        <>
          <strong>
            {showDirection ? 'Push' : null}
            {showType ? (showDirection ? ' update' : 'Updated') : null}
          </strong>
          {showSiteName ? (
            <>
              {' '}
              {showDirection ? 'from' : 'at'} <SiteName id={entity.targetSite.getId()} inline />
            </>
          ) : null}
        </>
      );
    }

    if (entity.type === SyndicationType.RetrieveAndDeleteEntity || entity.type === SyndicationType.RetrieveAndDeleteEmbeddedEntity) {
      return (
        <>
          <strong>
            {showDirection ? 'Push' : null}
            {showType ? (showDirection ? ' deletion' : 'Deleted') : null}
          </strong>{' '}
          {showEntityDetails ? (
            <>
              {' '}
              of {entity.rootEntityTypeVersion && <EntityTypeVersionName entityId={entity.rootEntityTypeVersion.getId()!} />}{' '}
              <em>{entity.rootEntityReference?.name}</em>
            </>
          ) : null}
          {showSiteName ? (
            <>
              {' '}
              at <SiteName id={entity.targetSite.getId()} inline />
            </>
          ) : null}
        </>
      );
    }

    if (entity.type === SyndicationType.PushEntity) {
      const isCloning = !!entity.isClone || !!entity.cloneEntity?.getId?.();

      return (
        <>
          <strong>
            {showDirection ? 'Pull' : null}
            {showType ? (isCloning ? (showDirection ? ' clone' : 'Clone') : showDirection ? ' update' : 'Update') : null}
          </strong>
          {showSiteName ? (
            <>
              {isCloning ? ' to' : showDirection ? ' into' : ''} <SiteName id={entity.targetSite.getId()} inline />
            </>
          ) : null}
        </>
      );
    }

    if (entity.type === SyndicationType.DeleteEntity || entity.type === SyndicationType.DeleteEmbeddedEntity) {
      return (
        <>
          <strong>
            {showDirection ? 'Pull' : null}
            {showType ? (showDirection ? ' deletion' : 'Delete') : null}
          </strong>{' '}
          {showEntityDetails ? (
            <>
              {' '}
              of {entity.rootEntityTypeVersion && <EntityTypeVersionName entityId={entity.rootEntityTypeVersion.getId()!} />}{' '}
              <em>{entity.rootEntityReference?.name}</em>
            </>
          ) : null}
          {showSiteName ? (
            <>
              {' '}
              {showDirection ? 'into' : 'at'} <SiteName id={entity.targetSite.getId()} inline />
            </>
          ) : null}
        </>
      );
    }

    if (entity.type === SyndicationType.RetrieveEntity) {
      return (
        <>
          <strong>Request</strong>
          {showEntityDetails ? ' entity details' : null}
          {showSiteName ? (
            <>
              {' '}
              from <SiteName id={entity.targetSite.getId()} inline />
            </>
          ) : null}
        </>
      );
    }

    if (entity.type === SyndicationType.RetrieveEntityList) {
      return (
        <>
          <strong>List</strong> entities
          {showSiteName ? (
            <>
              {' '}
              at <SiteName id={entity.targetSite.getId()} inline />
            </>
          ) : null}
        </>
      );
    }

    if (entity.type === SyndicationType.RetrieveConfig) {
      return (
        <>
          <strong>Sync</strong> config
          {showSiteName ? (
            <>
              {' '}
              at <SiteName id={entity.targetSite.getId()} inline />
            </>
          ) : null}
        </>
      );
    }

    return null;
  }
}
